import { Save as SaveIcon } from "@mui/icons-material";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../services/auth";
import usuarioService from "../services/usuario";
import { ESTADOS_BRASIL } from "../utils/constants";
import {
  formatCEP,
  formatCpfCnpj,
  formatPhone,
} from "../utils/formatters";
import CharCounter from "../components/CharCounter";
import { splitNomeCompleto } from "../utils/obterNomeExibicaoUsuario";

const CHAR_LIMITS = {
  razaoSocial: 150,
  nomeFantasia: 150,
  representanteLegal: 150,
  nome: 150,
  sobrenome: 150,
  email: 50,
  telefone: 15,
  rua: 100,
  numero: 10,
  municipio: 50,
  cep: 9,
};

const TIPO_USUARIO_PJ = 4;

const formatDataExibicao = (valor) => {
  if (!valor) return "";
  try {
    const data = typeof valor === "string" ? new Date(valor) : valor;
    if (isNaN(data.getTime())) return "";
    const dia = String(data.getDate()).padStart(2, "0");
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  } catch {
    return "";
  }
};

function AlterarDadosPessoaJuridica() {
  const navigate = useNavigate();
  const hasCalledAPI = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [errors] = useState({});
  const [semNumero, setSemNumero] = useState(false);
  const [errorsDeValidacao, setErrorsDeValidacao] = useState([]);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [emailOriginal, setEmailOriginal] = useState("");
  const [cnpjFormatado, setCnpjFormatado] = useState("");
  const [dataConstituicaoExibicao, setDataConstituicaoExibicao] = useState("");
  const [dataCadastroExibicao, setDataCadastroExibicao] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState({
    email: "",
    telefone: "",
    tipoUsuario: TIPO_USUARIO_PJ,
    endereco: { rua: "", municipio: "", estado: "", cep: "", numero: "" },
    pessoaJuridica: {
      razaoSocial: "",
      nomeFantasia: "",
      representanteLegal: "",
    },
  });

  useEffect(() => {
    if (!hasCalledAPI.current) {
      hasCalledAPI.current = true;
      carregarDados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarDados = async () => {
    try {
      const dados = await usuarioService.obterDados();

      const dadosFormatados = {
        email: dados.email || "",
        telefone: dados.telefone ? formatPhone(dados.telefone) : "",
        tipoUsuario: dados.tipoUsuario ?? TIPO_USUARIO_PJ,
        endereco: {
          rua: dados.endereco?.rua || "",
          numero: dados.endereco?.numero || "",
          municipio: dados.endereco?.municipio || "",
          estado: dados.endereco?.estado || "",
          cep: dados.endereco?.cep ? formatCEP(dados.endereco.cep) : "",
        },
        pessoaJuridica: {
          razaoSocial: dados.pessoaJuridica?.razaoSocial || "",
          nomeFantasia: dados.pessoaJuridica?.nomeFantasia || "",
          representanteLegal: dados.pessoaJuridica?.representanteLegal || "",
        },
      };

      setFormData(dadosFormatados);
      setEmailOriginal((dados.email || "").trim().toLowerCase());
      setCnpjFormatado(dados.cpf ? formatCpfCnpj(dados.cpf) : "");
      setDataConstituicaoExibicao(
        formatDataExibicao(dados.pessoaJuridica?.dataConstituicao),
      );
      setDataCadastroExibicao(formatDataExibicao(dados.createAt));
      setSemNumero(!dados.endereco?.numero);
      setLoading(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Erro ao carregar dados do usuário.",
        severity: "error",
      });
      setLoading(false);
    }
  };

  const handleSemNumeroChange = (e) => {
    const checked = e.target.checked;
    setSemNumero(checked);
    setFormData((prev) => ({
      ...prev,
      endereco: { ...prev.endereco, numero: checked ? null : "" },
    }));
  };

  const buscarCep = async (cepFormatado) => {
    const digits = cepFormatado.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setFormData((prev) => ({
          ...prev,
          endereco: {
            ...prev.endereco,
            rua: data.logradouro || prev.endereco.rua,
            municipio: data.localidade || prev.endereco.municipio,
            estado: data.uf || prev.endereco.estado,
          },
        }));
      }
    } catch {
    } finally {
      setCepLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    switch (name) {
      case "telefone":
        formattedValue = formatPhone(value);
        break;
      case "cep":
        formattedValue = formatCEP(value);
        buscarCep(formattedValue);
        break;
      default:
        formattedValue = value;
    }

    if (["cep", "rua", "municipio", "estado", "numero"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        endereco: { ...prev.endereco, [name]: formattedValue },
      }));
    } else if (
      ["razaoSocial", "nomeFantasia", "representanteLegal"].includes(name)
    ) {
      setFormData((prev) => ({
        ...prev,
        pessoaJuridica: { ...prev.pessoaJuridica, [name]: formattedValue },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: formattedValue }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrorsDeValidacao([]);

    try {
      const { nome, sobrenome } = splitNomeCompleto(
        formData.pessoaJuridica.representanteLegal,
        CHAR_LIMITS.nome,
      );
      const dataToSubmit = {
        nome,
        sobrenome,
        email: formData.email,
        telefone: formData.telefone,
        tipoUsuario: TIPO_USUARIO_PJ,
        pessoaJuridica: formData.pessoaJuridica,
        endereco: formData.endereco,
      };

      await usuarioService.atualizarDados(dataToSubmit);

      const emailAlterado =
        (formData.email || "").trim().toLowerCase() !== emailOriginal;

      if (emailAlterado) {
        setSnackbar({
          open: true,
          message:
            "Dados atualizados. Como o email foi alterado, voce sera deslogado para confirmar o novo email.",
          severity: "warning",
        });

        setTimeout(async () => {
          await authService.logout();
          navigate("/login", {
            state: {
              message:
                "Email alterado com sucesso. Confirme o novo email para voltar a acessar o sistema.",
            },
          });
        }, 5000);

        return;
      }

      setSnackbar({
        open: true,
        message: "Dados atualizados com sucesso!",
        severity: "success",
      });
      await carregarDados();
    } catch (error) {
      const validation = error.errors;
      if (validation) {
        const mensagens = Object.values(validation).flat();
        setErrorsDeValidacao(mensagens);
      } else if (error.message) {
        setErrorsDeValidacao([error.message]);
      } else {
        setErrorsDeValidacao(["Erro desconhecido."]);
      }
      setOpenErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" } }}
          >
            Alterar Dados da Empresa
          </Typography>

          <form onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Dados da Empresa
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CNPJ"
                  value={cnpjFormatado}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Data de Constituição"
                  value={dataConstituicaoExibicao}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Data de Cadastro"
                  value={dataCadastroExibicao}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Razão Social"
                  name="razaoSocial"
                  value={formData.pessoaJuridica.razaoSocial}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.razaoSocial }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.pessoaJuridica.razaoSocial}
                          maxLength={CHAR_LIMITS.razaoSocial}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Nome Fantasia"
                  name="nomeFantasia"
                  value={formData.pessoaJuridica.nomeFantasia}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.nomeFantasia }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.pessoaJuridica.nomeFantasia}
                          maxLength={CHAR_LIMITS.nomeFantasia}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Representante Legal"
                  name="representanteLegal"
                  value={formData.pessoaJuridica.representanteLegal}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.representanteLegal }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.pessoaJuridica.representanteLegal}
                          maxLength={CHAR_LIMITS.representanteLegal}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.email }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.email}
                          maxLength={CHAR_LIMITS.email}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Telefone"
                  name="telefone"
                  value={formData.telefone || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.telefone }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Typography variant="h6" gutterBottom>
                Endereço
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={semNumero}
                    onChange={handleSemNumeroChange}
                    sx={{
                      color: "#205723",
                      "&.Mui-checked": { color: "#205723" },
                    }}
                  />
                }
                label="Sem número"
              />
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  required
                  fullWidth
                  label="Rua"
                  name="rua"
                  value={formData.endereco.rua || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.rua }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.endereco.rua}
                          maxLength={CHAR_LIMITS.rua}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  required
                  fullWidth
                  label="Número"
                  name="numero"
                  value={formData.endereco.numero ?? ""}
                  onChange={handleChange}
                  disabled={semNumero}
                  error={!!errors.numero}
                  inputProps={{ maxLength: CHAR_LIMITS.numero }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Cidade"
                  name="municipio"
                  value={formData.endereco.municipio || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.municipio }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    name="estado"
                    value={formData.endereco.estado || ""}
                    onChange={handleChange}
                    label="Estado"
                  >
                    {ESTADOS_BRASIL.map((estado) => (
                      <MenuItem key={estado.sigla} value={estado.sigla}>
                        {estado.sigla}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  required
                  fullWidth
                  label="CEP"
                  name="cep"
                  value={formData.endereco.cep || ""}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.cep }}
                  InputProps={{
                    endAdornment: cepLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>
            </Grid>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              spacing={2}
              sx={{ mt: 4 }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={<SaveIcon />}
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {saving ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Modal
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={openErrorModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95vw", sm: 600, md: 800 },
              maxWidth: "95vw",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: { xs: 2, sm: 4 },
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Erro ao atualizar dados:
            </Typography>
            <ul>
              {errorsDeValidacao.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() => setOpenErrorModal(false)}
            >
              Fechar
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Container>
  );
}

export default AlterarDadosPessoaJuridica;
